import React, {useEffect, useState} from 'react';
import {Checkbox, Flex, Form, Input, message, Modal, Select} from "antd";
import {useDispatch} from "react-redux";
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import classes from "../../Courses/CreateCourse/CreateCourse.module.css";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import FormItem from "antd/es/form/FormItem";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {clrs} from "../../../../../constants/colors";
import UserService from "../../../../../services/UserService";
import GroupService from "../../../../../services/GroupService";
import {LocalName} from "../../../../../utils/LocalName";

const CreateUsers = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const firstName = Form.useWatch('firstName', form);
    const lastName = Form.useWatch('lastName', form);
    const middleName = Form.useWatch('middleName', form);
    const email = Form.useWatch('email', form);
    const password = Form.useWatch('password', form);
    const role = Form.useWatch('role', form);
    const autoPassword = Form.useWatch('autoPassword', form);
    const sendCredentials = Form.useWatch('sendCredentials', form);
    const groupId = Form.useWatch('groupId', form);

    const [isLoadingCreate, setIsLoadingCreate] = useState(false);
    const [groups, setGroups] = useState([]);

    // IntlMessageText.getText внутри вызывает хук useTranslation, поэтому все тексты
    // вычисляем безусловно и один раз за рендер. Вызов из условной ветки JSX или из
    // обработчика меняет количество хуков между рендерами и роняет страницу в белый экран.
    const lastNameText = IntlMessageText.getText({id: "lastName"});
    const firstNameText = IntlMessageText.getText({id: "firstName"});
    const middleNameText = IntlMessageText.getText({id: "middleName"});
    const emailText = IntlMessageText.getText({id: "email"});
    const passwordText = IntlMessageText.getText({id: "password"});
    const roleText = IntlMessageText.getText({id: "role"});
    const groupText = IntlMessageText.getText({id: "createUsers.group"});
    const credentialsTitleText = IntlMessageText.getText({id: "createUsers.credentialsTitle"});
    const emailedNoteText = IntlMessageText.getText({id: "createUsers.emailedNote"});

    useEffect(() => {
        // п.5.4: список групп для мгновенного добавления ученика.
        // Грузим только когда выбрана роль «ученик»: /api/admin/group/get?limit=1000 отдаёт
        // ~1.5 МБ за ~23 с (в каждой группе сериализуется весь список студентов),
        // а для преподавателя и mEditor список групп не нужен вовсе.
        if (role !== 'student') {
            return;
        }
        GroupService.getGroups(1, 1000)
            .then(({data}) => setGroups(data || []))
            .catch(() => setGroups([]));
    }, [role]);

    function handleCreate() {
        form.validateFields()
            .then(async () => {
                message.loading(<IntlMessage id={'loading'}/>, 0);
                setIsLoadingCreate(true);
                try {
                    // п.5.5: при автогенерации отправляем пустой пароль — бэкенд сгенерирует
                    const result = await UserService.saveUser(
                        firstName, lastName, middleName, email,
                        autoPassword ? '' : password,
                        role,
                        sendCredentials !== false
                    );

                    // п.5.4: сразу добавляем ученика в выбранную группу
                    if (role === 'student' && groupId && result?.userId) {
                        try {
                            await GroupService.addStudents(groupId, [result.userId]);
                        } catch (e) {
                            message.warning(<IntlMessage id={'createUsers.groupAddFailed'}/>, 5);
                        }
                    }

                    message.destroy();
                    message.success(<IntlMessage id={'creation'}/>, 3);

                    // Показываем администратору логин и пароль
                    Modal.success({
                        title: credentialsTitleText,
                        content: (
                            <div>
                                <p><b>{emailText}:</b> {result?.username}</p>
                                <p><b>{passwordText}:</b> {result?.password}</p>
                                {result?.emailed &&
                                    <p style={{color: clrs.blackerBlue}}>
                                        {emailedNoteText}
                                    </p>}
                            </div>
                        ),
                    });
                    form.resetFields();
                } catch (e) {
                    message.destroy();
                    message.error(<IntlMessage id={'error'}/>, 5);
                } finally {
                    setIsLoadingCreate(false);
                }
            })
            .catch(() => {
                console.log("Validation error!")
            })
    }

    return (
        <Form form={form} className={classes.form}
              initialValues={{autoPassword: true, sendCredentials: true}}>
            <BlockLoading isLoading={isLoadingCreate}/>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'lastName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="lastName">
                <Input rootClassName={classes.formInput} placeholder={lastNameText} />
            </FormItem>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'firstName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="firstName">
                <Input rootClassName={classes.formInput} placeholder={firstNameText} />
            </FormItem>
            <FormItem rules={[{
                required: false,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'middleName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="middleName">
                <Input rootClassName={classes.formInput} placeholder={middleNameText} />
            </FormItem>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'email'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="email">
                <Input rootClassName={classes.formInput} placeholder={emailText} />
            </FormItem>

            <FormItem name="autoPassword" valuePropName="checked" className={classes.formInputBlock}>
                <Checkbox><IntlMessage id={'createUsers.autoPassword'}/></Checkbox>
            </FormItem>

            {!autoPassword &&
                <FormItem rules={[{
                    required: true,
                    message: <IntlMessage id={'requiredField'}/>,
                },
                ]} label={<IntlMessage id={'password'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="password">
                    <Input rootClassName={classes.formInput} placeholder={passwordText} />
                </FormItem>}

            <FormItem name="sendCredentials" valuePropName="checked" className={classes.formInputBlock}>
                <Checkbox><IntlMessage id={'createUsers.sendCredentials'}/></Checkbox>
            </FormItem>

            <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                      className={classes.formInput} style={{width: '100%', padding: 0}} name="role"
                      label={<IntlMessage id={'role'}/>} labelCol={{ span: 24 }}
            >
                <Select
                    options={[
                    { value: 'teacher', label: <IntlMessage id={"ordersPage.teacher"}/> },
                    { value: 'student', label: <IntlMessage id={"student"}/> },
                    { value: 'mEditor', label: <IntlMessage id={"mEditor"}/> }
                    ]}
                    placeholder={roleText}/>
            </FormItem>

            {role === 'student' &&
                <FormItem className={classes.formInput} style={{width: '100%', padding: 0}} name="groupId"
                          label={<IntlMessage id={'createUsers.group'}/>} labelCol={{ span: 24 }}
                >
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={groups.map(g => ({
                            value: g.id,
                            label: `${LocalName.getName(g)}${g.number ? ' №' + g.number : ''}`
                        }))}
                        placeholder={groupText}/>
                </FormItem>}

            <FormItem className={classes.formInputBlock}>
                <Flex align={"flex-start"} justify={"flex-start"} gap={20} style={{width: "100%"}}>
                    <GreyButton onClick={handleCreate} backgroundColor={clrs.blackerBlue} fontColor={clrs.white}><IntlMessage id={'create'}/></GreyButton>
                </Flex>
            </FormItem>
        </Form>
    );
};

export default CreateUsers;
